import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-slate-100" data-testid="terms-of-use-page">
      <PublicNav currentPage="terms-of-use" />

      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 text-sm">
          <ArrowLeft className="mr-2" size={16} />
          Back to Home
        </Link>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Terms of Use</h1>
        <p className="text-slate-600 mb-8">Last updated: February 2026</p>

        <div className="bg-white border border-slate-200 shadow-sm p-8 rounded-lg space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">1. Introduction</h2>
            <p className="text-blue-800 leading-relaxed">
              Welcome to SeedSynch. These Terms of Use ("Terms") govern your access to and use of the SeedSynch platform, including our website, services, and any related applications (collectively, the "Platform"). By creating an account or using the Platform, you agree to be bound by these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">2. Platform Status</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 font-medium">
                SeedSynch is currently in early-stage development (private beta). Features, availability, and functionality may change without notice.
              </p>
            </div>
            <p className="text-blue-800 leading-relaxed">
              We are continuously improving the Platform and appreciate your patience and feedback during this phase. Some features may be incomplete, experimental, or subject to modification.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">3. Account Registration</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              To use SeedSynch, you must:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">4. Acceptable Use</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree NOT to:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li>Provide false, misleading, or fraudulent information in your profile or communications</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
              <li>Use the Platform for any illegal, harmful, or unauthorized purpose</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Share confidential information obtained through NDAs outside the agreed purposes</li>
              <li>Attempt to gain unauthorized access to the Platform or other users' accounts</li>
              <li>Use automated systems (bots, scrapers) to access or collect data from the Platform</li>
              <li>Interfere with or disrupt the Platform's operation or security</li>
              <li>Circumvent any access restrictions or security measures</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">5. User Content</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              You retain ownership of content you submit to the Platform (profiles, pitch decks, messages). By submitting content, you grant SeedSynch a limited license to:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li>Display your profile to other users for matchmaking purposes</li>
              <li>Store and process your content to provide our services</li>
              <li>Share your content with users who have accepted applicable NDAs</li>
            </ul>
            <p className="text-blue-800 leading-relaxed mt-4">
              You are solely responsible for the accuracy and legality of content you submit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">6. NDA and Confidentiality</h2>
            <p className="text-blue-800 leading-relaxed">
              The Platform includes a built-in NDA workflow to protect sensitive information. When you accept an NDA to access another user's confidential materials, you agree to be bound by the terms of that NDA. Breach of NDA obligations may result in account termination and potential legal liability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">7. No Investment Advice</h2>
            <p className="text-blue-800 leading-relaxed">
              SeedSynch is a matchmaking platform only. We do not provide investment advice, endorse any startup or investor, or guarantee any outcomes. All investment decisions are made solely between users at their own risk. You should conduct your own due diligence before entering into any business relationship or investment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">8. Limitation of Liability</h2>
            <div className="bg-slate-100/50 border border-slate-300 rounded-lg p-4">
              <p className="text-blue-800 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SEEDSYNCH AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4 mt-4">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Any damages arising from your use of or inability to use the Platform</li>
                <li>Any conduct or content of any third party on the Platform</li>
                <li>Any failure or delay in matchmaking or introductions</li>
                <li>The outcome of any business relationship or investment facilitated through the Platform</li>
              </ul>
              <p className="text-blue-800 leading-relaxed mt-4">
                Our total liability for any claims arising from these Terms or your use of the Platform shall not exceed the amount you paid us (if any) in the 12 months preceding the claim.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-blue-800 leading-relaxed">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. AS AN EARLY-STAGE PLATFORM, FEATURES AND AVAILABILITY MAY CHANGE WITHOUT NOTICE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">10. Termination</h2>
            <p className="text-blue-800 leading-relaxed">
              We may suspend or terminate your account at any time for violation of these Terms or for any other reason at our discretion. You may also delete your account at any time. Upon termination, your right to use the Platform will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">11. Changes to Terms</h2>
            <p className="text-blue-800 leading-relaxed">
              We may modify these Terms at any time. We will notify you of material changes by posting updated Terms on the Platform. Your continued use of the Platform after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">12. Governing Law</h2>
            <p className="text-blue-800 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">13. Contact Us</h2>
            <p className="text-blue-800 leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-blue-800 mt-4">
              <strong className="text-blue-900">Email:</strong> <a href="mailto:hello@seedsynch.com" className="text-blue-500 hover:text-blue-300">hello@seedsynch.com</a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
